import React, { useState } from 'react';
import './RegionExplanation.css';

export default function RegionExplanation({ region, fields, getRegionColor }) {
  const [showExplanation, setShowExplanation] = useState(true);
  
  if (!region) {
    return (
      <div className="region-explanation empty">
        <p className="empty-state">
          Select a region to see details and explanation
        </p>
      </div>
    );
  }
  
  const color = getRegionColor(region.region_type);
  const regionFields = fields.filter(f => f.region_id === region.id);
  
  // Generate explanation based on region properties
  const reasons = [];
  
  if (region.confidence >= 0.9) {
    reasons.push('High confidence region detection');
  } else if (region.confidence >= 0.7) {
    reasons.push('Moderate confidence region detection');
  }
  
  if (region.anchor_text) {
    reasons.push(`Anchor text "${region.anchor_text}" found in document`);
  }
  
  if (region.region_type === 'header') {
    reasons.push('Located in top portion of document');
    reasons.push('Contains identifying information (invoice #, date, etc.)');
  } else if (region.region_type === 'totals') {
    reasons.push('Contains currency-formatted numbers');
    reasons.push('Located near bottom of document');
    reasons.push('Keywords like "Total", "Subtotal" detected');
  } else if (region.region_type === 'line_items') {
    reasons.push('Table structure detected');
    reasons.push('Multiple rows with consistent columns');
  } else if (region.region_type === 'parties') {
    reasons.push('Address-like formatting detected');
    reasons.push('Contains "Bill To", "Ship To", or company name keywords');
  } else if (region.region_type === 'footer') {
    reasons.push('Located in bottom portion of document');
    reasons.push('Contains notes, terms, or payment information');
  } else if (region.region_type === 'custom') {
    reasons.push('Custom region defined outside standard categories');
  }
  
  if (regionFields.length > 0) {
    reasons.push(`${regionFields.length} field(s) detected within this region`);
  }
  
  const confidenceColor = region.confidence >= 0.9 ? '#22c55e' :
                         region.confidence >= 0.7 ? '#f59e0b' : '#ef4444';
  
  return (
    <div className="region-explanation">
      <div className="region-expl-header" style={{ borderLeftColor: color.border }}>
        <div className="region-title">
          <span className="region-title-icon">{color.icon}</span>
          <h4>{region.label}</h4>
        </div>
        {region.confidence != null && (
          <span 
            className="confidence-large"
            style={{ backgroundColor: confidenceColor }}
          >
            {Math.round(region.confidence * 100)}%
          </span>
        )}
      </div>
      
      {region.description && (
        <p className="region-description">{region.description}</p>
      )}
      
      <div className="region-details">
        <div className="detail-row">
          <span className="detail-label">Type:</span>
          <span className="detail-value">{region.region_type}</span>
        </div>
        <div className="detail-row">
          <span className="detail-label">Origin:</span>
          <span className="detail-value">
            {region.origin === 'ai' ? '🤖 AI Detected' : '👤 User Defined'}
          </span>
        </div>
        <div className="detail-row">
          <span className="detail-label">Fields:</span>
          <span className="detail-value">{regionFields.length}</span>
        </div>
        <div className="detail-row">
          <span className="detail-label">Page:</span>
          <span className="detail-value">{(region.page_number ?? 0) + 1}</span>
        </div>
        {region.anchor_text && (
          <div className="detail-row">
            <span className="detail-label">Anchor:</span>
            <span className="detail-value detail-value-code">{region.anchor_text}</span>
          </div>
        )}
        {region.anchor_strategy && (
          <div className="detail-row">
            <span className="detail-label">Match:</span>
            <span className="detail-value">{region.anchor_strategy}</span>
          </div>
        )}
      </div>
      
      <div className="region-geometry">
        <span className="detail-label">Geometry:</span>
        <div className="geometry-grid">
          <span>x: {Number(region.x).toFixed(3)}</span>
          <span>y: {Number(region.y).toFixed(3)}</span>
          <span>w: {Number(region.width).toFixed(3)}</span>
          <span>h: {Number(region.height).toFixed(3)}</span>
        </div>
      </div>
      
      <button 
        className="explanation-toggle"
        onClick={() => setShowExplanation(!showExplanation)}
      >
        {showExplanation ? '▼' : '▶'} Why I think this
      </button>
      
      {showExplanation && (
        <div className="explanation-content">
          <ul className="reasons-list">
            {reasons.map((reason, i) => (
              <li key={i}>
                <span className="check">✓</span>
                <span>{reason}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      
      <div className="region-fields-list">
        <h5>Fields in this region</h5>
        {regionFields.length === 0 ? (
          <p className="no-fields-msg">No fields assigned</p>
        ) : (
          <ul>
            {regionFields.map(field => (
              <li key={field.id}>
                <span className="rfl-field-name">{field.label || field.field_key}</span>
                <span className="rfl-field-type">{field.data_type || field.value_type || 'text'}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
